import {
	LOTTIE_COLOR_MATCH_EPSILON,
	LOTTIE_TEMPLATE_BRAND_PURPLE,
	LOTTIE_TEMPLATE_ICON_GRAY,
	LOTTIE_TEMPLATE_OVAL_DOTS,
	LOTTIE_TEMPLATE_PRUEBA_VIDA_DARK_BG,
	LOTTIE_TEMPLATE_PRUEBA_VIDA_LIGHT_BG,
} from './lottie-template-colors';

export interface LottieResolvedThemeColors {
	readonly borderRgb: [number, number, number] | null;
	readonly iconBorderRgb: [number, number, number] | null;
	readonly backgroundRgb: [number, number, number] | null;
	/** Borde secundario → puntos del óvalo (`upper-line` / `low-line`). */
	readonly secondaryBorderRgb: [number, number, number] | null;
}

function nearRgb(a: readonly number[], b: readonly number[], eps: number): boolean {
	return (
		Math.abs(a[0] - b[0]) <= eps &&
		Math.abs(a[1] - b[1]) <= eps &&
		Math.abs(a[2] - b[2]) <= eps
	);
}

function isNearWhite(k: readonly number[]): boolean {
	return k[0] > 0.995 && k[1] > 0.995 && k[2] > 0.995;
}

/**
 * Recorre capas Bodymovin (incl. assets/precomps) y aplica colores de tema.
 * Muta el objeto en profundidad (clonar antes si se reutiliza la respuesta HTTP cacheada).
 */
export function applyLottieThemeToJson(root: unknown, theme: LottieResolvedThemeColors): void {
	if (!root || typeof root !== 'object') {
		return;
	}
	const data = root as Record<string, unknown>;
	const topLayers = data['layers'];
	if (Array.isArray(topLayers)) {
		for (const layer of topLayers) {
			walkLayer(layer, []);
		}
	}
	const assets = data['assets'];
	if (Array.isArray(assets)) {
		for (const asset of assets) {
			if (asset && typeof asset === 'object') {
				const assetRec = asset as Record<string, unknown>;
				const nestedLayers = assetRec['layers'];
				if (Array.isArray(nestedLayers)) {
					for (const layer of nestedLayers) {
						walkLayer(layer, []);
					}
				}
			}
		}
	}

	function walkLayer(layer: unknown, _stack: string[]): void {
		if (!layer || typeof layer !== 'object') {
			return;
		}
		const ly = layer as Record<string, unknown>;
		const layerNmRaw = ly['nm'];
		const layerNm = typeof layerNmRaw === 'string' ? layerNmRaw : '';
		const layerStack = layerNm ? [layerNm] : [];
		const layerShapes = ly['shapes'];
		if (Array.isArray(layerShapes)) {
			for (const shape of layerShapes) {
				walkShapeGroup(shape, layerStack);
			}
		}
	}

	function walkShapeGroup(node: unknown, groupStack: string[]): void {
		if (!node || typeof node !== 'object') {
			return;
		}
		const o = node as Record<string, unknown>;
		const nmRaw = o['nm'];
		const nm = typeof nmRaw === 'string' ? nmRaw : '';
		const ty = o['ty'];
		const nextStack = ty === 'gr' && nm ? [...groupStack, nm] : groupStack;

		if (ty === 'fl' || ty === 'st') {
			applyColorProp(o, ty as string, nextStack);
		}
		const items = o['it'];
		if (Array.isArray(items)) {
			for (const child of items) {
				walkShapeGroup(child, nextStack);
			}
		}
	}

	function applyColorProp(shapeObj: Record<string, unknown>, ty: string, stack: string[]): void {
		const c = shapeObj['c'];
		if (!c || typeof c !== 'object') {
			return;
		}
		const colorProp = c as { a?: number; k?: unknown };
		if (colorProp.a !== 0 || !colorProp.k) {
			return;
		}
		const k = colorProp.k;
		if (!Array.isArray(k) || k.length < 3) {
			return;
		}
		const rgb = [Number(k[0]), Number(k[1]), Number(k[2])];
		const next = resolveReplacement(rgb, ty, stack);
		if (!next) {
			return;
		}
		k[0] = next[0];
		k[1] = next[1];
		k[2] = next[2];
	}

	function isOvalDotsLayer(stack: string[]): boolean {
		return stack.some((name) => /(?:^|\/)(?:upper|low)-line\b/i.test(name) || /(?:upper|low)-line Outlines/i.test(name));
	}

	function resolveReplacement(
		rgb: number[],
		ty: string,
		stack: string[],
	): [number, number, number] | null {
		const eps = LOTTIE_COLOR_MATCH_EPSILON;
		if (nearRgb(rgb, LOTTIE_TEMPLATE_BRAND_PURPLE, eps)) {
			return theme.borderRgb ?? null;
		}
		if (nearRgb(rgb, LOTTIE_TEMPLATE_ICON_GRAY, eps)) {
			return theme.iconBorderRgb ?? null;
		}
		// Óvalo de puntos: por capa (dark low-line reusa el bg) o por color canónico light.
		if (ty === 'fl' && (isOvalDotsLayer(stack) || nearRgb(rgb, LOTTIE_TEMPLATE_OVAL_DOTS, eps))) {
			return theme.secondaryBorderRgb ?? null;
		}
		if (nearRgb(rgb, LOTTIE_TEMPLATE_PRUEBA_VIDA_LIGHT_BG, eps) || nearRgb(rgb, LOTTIE_TEMPLATE_PRUEBA_VIDA_DARK_BG, eps)) {
			return theme.backgroundRgb ?? null;
		}
		if (ty === 'fl' && isNearWhite(rgb) && stack.includes('Group 25')) {
			return theme.backgroundRgb ?? null;
		}
		return null;
	}
}
